import { useEffect } from "react";
import LiveScores from "./components/LiveScores";
import { motion } from "framer-motion";

export default function Portfolio() {
  useEffect(() => {
    document.title = "Sebastian Baldeon";
  }, []);

  return (
    <main className="min-h-screen bg-black text-white font-sans">
      {/* Navbar */}
      <nav className="flex justify-between items-center px-6 py-6 fixed top-0 left-0 right-0 z-50 backdrop-blur bg-black/70 border-b border-gray-800">
        <h1 className="text-xl font-bold tracking-wide">Sebastian Baldeon</h1>
        <ul className="flex gap-6 text-sm font-medium">
          {[
            ["About", "#about"],
            ["Skills", "#skills"],
            ["Projects", "#projects"],
            ["Resume", "#resume"],
            ["Contact", "#contact"],
            ["Live Scores", "#scores"],
          ].map(([text, link]) => (
            <li key={text}>
              <a
                href={link}
                className="text-gray-400 hover:text-white transition-colors"
              >
                {text}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <div className="pt-24 px-6 max-w-6xl mx-auto space-y-24">
        {/* Hero Section */}
        <motion.section
          id="about"
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <h2 className="text-5xl font-bold mb-4 text-white">
            Hello, I'm <span className="text-white">Sebastian</span>.
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            I'm a Computer Science student at UVA with a passion for building
            impactful software. Whether through machine learning, system
            design, or research, I bring creativity and technical depth to
            everything I do.
          </p>
          <a
            href="#projects"
            className="inline-block mt-6 px-6 py-2 border border-gray-500 text-gray-300 rounded hover:bg-gray-700 hover:text-white transition"
          >
            View my work ↓
          </a>
        </motion.section>

        {/* Skills Section */}
        <section id="skills">
          <h2 className="text-2xl font-bold mb-6 border-b border-gray-700 pb-2">
            Skills
          </h2>
          <ul className="flex flex-wrap gap-4 text-gray-300">
            {[
              "Java",
              "Python",
              "C",
              "MATLAB",
              "Arduino Programming",
              "AutoDesk CAD",
              "MS Office",
              "Excel",
              "Machine Learning",
              "Git",
            ].map((skill) => (
              <li
                key={skill}
                className="bg-gray-800 px-4 py-2 rounded-lg text-sm hover:bg-gray-700 transition"
              >
                {skill}
              </li>
            ))}
          </ul>
        </section>

        {/* Projects Section */}
        <section id="projects">
          <h2 className="text-2xl font-bold mb-6 border-b border-gray-700 pb-2">
            Selected Projects
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                title: "Fecal Egg Count Detection with CNNs",
                desc:
                  "Used deep learning to count parasite eggs in microscope images. Built a CNN model and web prototype. Limited by data, but laid strong groundwork for low-cost diagnostics.",
                link: "/Eggs.pdf",
              },
              {
                title: "Predicting Chess Game Outcomes",
                desc:
                  "Analyzed historical chess data to predict match outcomes using ML models. Explored Elo ratings, opening strategies, and performance trends.",
                link: "/chess-presentation.pdf",
              },
              {
                title: "Live Soccer Scores Viewer",
                desc:
                  "Integrated football-data.org API to fetch and display real-time soccer scores. Features clean Tailwind UI and React logic for dynamic updates.",
                link: "#scores",
              },
            ].map(({ title, desc, link }, i) => (
              <motion.a
                key={title}
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-gray-900 p-6 rounded-xl border border-gray-800 hover:border-gray-500 hover:bg-gray-800 transition transform hover:scale-105 shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <h3 className="text-lg font-semibold text-white">{title}</h3>
                <p className="text-gray-300 mt-2">{desc}</p>
              </motion.a>
            ))}
          </div>
        </section>

        {/* Live Scores */}
        <section id="scores">
          <LiveScores />
        </section>

        {/* Resume */}
        <section id="resume">
          <h2 className="text-2xl font-bold mb-4 border-b border-gray-700 pb-2">
            Resume
          </h2>
          <a
            href="/resume.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-gray-800 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded transition"
          >
            Download My Resume
          </a>
        </section>

        {/* Contact */}
        <section id="contact">
          <h2 className="text-2xl font-bold mb-4 border-b border-gray-700 pb-2">
            Contact
          </h2>
          <p className="text-gray-400 mb-2">
            Let's connect! I'm always open to new opportunities or
            collaborations.
          </p>
          <p className="text-white font-medium">
            sebastianbaldeon57@gmail.com
          </p>
          <div className="flex space-x-4 mt-4">
            <a
              href="https://www.linkedin.com/in/sebastian-baldeon-91452a246/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded"
            >
              LinkedIn
            </a>
            <a
              href="https://github.com/sebastianbaldeon"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded"
            >
              GitHub
            </a>
          </div>
        </section>

        {/* Profile Image */}
        <motion.div
          className="flex justify-center mt-12"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <img
            src="/profile.png"
            alt="Sebastian Baldeon"
            className="w-40 h-40 rounded-full border-4 border-gray-600 shadow-lg object-cover"
          />
        </motion.div>
      </div>
    </main>
  );
}
